import React from 'react';
import { Modal } from '../../admin/Modal';
import { AlertTriangle, TrendingUp, TrendingDown } from 'lucide-react';

interface ConfirmPhaseChangeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  currentPhase: number;
  newPhase: number;
  clientName: string;
}

export const ConfirmPhaseChangeModal: React.FC<ConfirmPhaseChangeModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  currentPhase,
  newPhase,
  clientName
}) => {
  const isAdvancing = newPhase > currentPhase;
  const isRegressing = newPhase < currentPhase;

  const getPhaseImpact = (phase: number): string => {
    const impacts: Record<number, string> = {
      0: 'Initial onboarding - limited goal availability',
      1: 'Early program - additional goals become available',
      2: 'Intermediate program - more goals available',
      3: 'Advanced program - all goals available'
    };
    return impacts[phase] || '';
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isAdvancing ? 'Advance Client Phase?' : 'Change Client Phase?'}
      variant={isRegressing ? 'danger' : 'warning'}
      size="small"
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className={isRegressing ? 'btn btn-danger' : 'btn btn-primary'}
            onClick={onConfirm}
          >
            {isRegressing ? 'Regress Phase' : 'Confirm Change'}
          </button>
        </>
      }
    >
      <div style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          {isRegressing ? (
            <TrendingDown size={20} color="var(--danger)" />
          ) : isAdvancing ? (
            <TrendingUp size={20} color="var(--success)" />
          ) : (
            <AlertTriangle size={20} color="var(--warning)" />
          )}
          <span style={{ fontWeight: '600', color: 'var(--text-primary)' }}>
            {isAdvancing && `Ready to advance ${clientName} to Phase ${newPhase}?`}
            {isRegressing && `Warning: Regressing ${clientName} from Phase ${currentPhase} to Phase ${newPhase}`}
            {!isAdvancing && !isRegressing && `Confirm phase change for ${clientName}`}
          </span>
        </div>

        {isRegressing && (
          <div
            style={{
              marginLeft: '28px',
              marginBottom: '16px',
              padding: '12px',
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              borderRadius: '8px',
              border: '1px solid rgba(239, 68, 68, 0.3)'
            }}
          >
            <p style={{ fontSize: '14px', color: 'var(--danger)', margin: 0 }}>
              <strong>Regressing to an earlier phase may limit client access to goals and features.</strong>
            </p>
          </div>
        )}

        <div style={{ marginLeft: '28px', color: 'var(--text-secondary)', fontSize: '14px' }}>
          <p style={{ margin: '8px 0', fontWeight: '600', color: 'var(--text-primary)' }}>
            Phase {currentPhase} → Phase {newPhase}
          </p>

          <div style={{ margin: '12px 0' }}>
            <p style={{ margin: '4px 0', fontSize: '13px' }}>
              <strong>From:</strong> {getPhaseImpact(currentPhase)}
            </p>
            <p style={{ margin: '4px 0', fontSize: '13px' }}>
              <strong>To:</strong> {getPhaseImpact(newPhase)}
            </p>
          </div>

          {isAdvancing && (
            <div
              style={{
                marginTop: '12px',
                padding: '12px',
                backgroundColor: 'rgba(16, 185, 129, 0.1)',
                borderRadius: '8px',
                border: '1px solid rgba(16, 185, 129, 0.3)'
              }}
            >
              <p style={{ fontSize: '13px', color: 'var(--success)', margin: 0 }}>
                Client will gain access to additional program features and goals.
              </p>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};
